import { useMemo, useRef, useEffect } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, usePropertyField } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { btnOnDark } from '../utils/buttonStyles';
import { ctaLinkProps } from '../utils/scheduleTourModal';

/**
 * Formats a raw bedrooms range (e.g. "studio - 2") into natural copy
 * like "Studio - 2 Bedroom". Handles single values ("1" → "1 Bedroom"),
 * ranges ("1 - 3" → "1 - 3 Bedroom"), and studios. Equal endpoints
 * collapse to a single value (e.g. "2 - 2" → "2 Bedroom",
 * "Studio - Studio" → "Studio").
 */
function formatBedroomsLabel(raw: string | null | undefined): string {
  if (!raw) return 'Apartments';
  const trimmed = raw.trim();
  if (!trimmed) return 'Apartments';

  const parts = trimmed.split(/\s*-\s*/);
  const formatted = parts.map((p) => {
    const lower = p.trim().toLowerCase();
    if (lower === 'studio' || lower === 'std') return 'Studio';
    return p.trim();
  });

  // Collapse equal endpoints: "2 - 2" → "2", "Studio - Studio" → "Studio".
  // Compare case-insensitively so "studio - Studio" still collapses cleanly.
  const deduped =
    formatted.length > 1 && formatted.every((p) => p.toLowerCase() === formatted[0].toLowerCase())
      ? [formatted[0]]
      : formatted;

  const label = deduped.join(' - ');
  const allStudios = deduped.every((p) => p === 'Studio');
  return allStudios ? `${label}` : `${label} Bedroom`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Availability',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  const bedroomsRange = usePropertyField('property_bedrooms_range');
  const city = usePropertyField('property_address_city');
  const state = usePropertyField('property_address_state');

  // Parallax refs
  const sectionRef = useRef<HTMLElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    // Respect reduced-motion preference
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      // rect.top < 0 once the section has scrolled above the viewport top
      const scrolled = -rect.top;
      // Only compute while the section is at least partially visible
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      // Shift the image down at 30 % of scroll speed, creating the parallax depth
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // seed the position on mount (handles pre-scrolled pages)
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const subtitleHtml = useMemo(() => {
    const beds = formatBedroomsLabel(bedroomsRange as string | null);
    const cityStr = city || '';
    const stateStr = state || '';
    const location = [cityStr, stateStr].filter(Boolean).join(', ');
    return `<p>${beds} Apartments for Rent${location ? ` in ${location}` : ''}</p>`;
  }, [bedroomsRange, city, state]);

  return (
    // Mobile: special is fixed and covers the top of the page, so push the
    // hero down by --offer-banner-height so it starts right after the special
    // ends. Desktop (lg+) already gets that offset via the header spacer.
    <section
      ref={sectionRef}
      className="relative w-full h-[72svh] min-h-[26rem] md:h-screen md:min-h-0 overflow-hidden mt-[var(--offer-banner-height,0px)] lg:mt-0"
      style={{ transition: 'margin-top 0.5s cubic-bezier(0.4, 0, 0.2, 1)' }}
    >
      {/* Parallax background */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        {/* Oversized wrapper: 140 % tall starting 20 % above the section top.
            The extra height gives the parallax translate room to move. */}
        <div ref={imgWrapRef} className="absolute inset-x-0 h-[140%]" style={{ top: '-20%', willChange: 'transform' }}>
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{ imageRendering: 'high-quality' as any }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast — purely decorative, must not
          intercept clicks (otherwise floating chatbot iframes / widgets
          rendered above the hero can't be clicked through the empty
          gradient area). */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20 pointer-events-none" />

      {/* Content. The flex container fills the section (h-full) so children
          can be vertically centered, but we disable pointer events on the
          wrapper itself and re-enable them only on the actual interactive
          children. That way empty space in the hero passes clicks through
          to anything floating on top (e.g. the RentCafe CRM IQ chatbot).
          Mobile spacing is intentionally tight so the copy + CTA read as
          one compact cluster; desktop keeps the original loftier rhythm. */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pointer-events-none">
        <RichTextContent
          path="hero.pre_title"
          label="Pre-Title"
          defaultValue='<p>Welcome to</p>'
          as="p"
          className="hero-entrance hero-entrance-1 font-body text-sm sm:text-base md:text-lg tracking-[0.25em] uppercase opacity-80 mb-1 md:mb-2 drop-shadow-md pointer-events-auto"
        />
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-2 md:mb-5 max-w-5xl leading-[1.1] tracking-tight drop-shadow-lg uppercase pointer-events-auto"
        />

        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleHtml}
          as="p"
          className="hero-entrance hero-entrance-3 font-body text-base sm:text-lg md:text-2xl max-w-5xl mb-5 md:mb-10 leading-snug md:leading-relaxed tracking-[0.12em] md:tracking-normal opacity-90 drop-shadow-md uppercase pointer-events-auto"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            {...ctaLinkProps(ctaLink.url || '/floor-plans', ctaLink._target)}
            className={`hero-entrance hero-entrance-4 relative inline-flex items-center gap-2.5 px-7 py-2.5 md:px-10 md:py-4 text-sm font-medium tracking-[0.2em] uppercase pointer-events-auto ${btnOnDark}`}
          >
            <span className="relative z-10">{ctaLink.label || 'View Availability'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.pre_title': {
      label: 'Pre-Title',
      type: 'richtext',
      defaultValue: '<p>Welcome to</p>',
    },
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Apartments for Rent</p>',
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
